/* eslint-disable i18next/no-literal-string */
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer'
import { Document, BLOCKS, MARKS } from '@contentful/rich-text-types'
import classnames from 'classnames'
import { TFunction, useTranslation } from 'next-i18next'
import React, {
  ForwardedRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { CONFIG_ENABLE_HOLIDAY_CUTOFF } from '@src/config/env'
import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { STATSIG_EXPERIMENTS, useStatsigExperimentBucket } from '@src/experiments/statsig-integration'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import type { default as ContentfulProductMeasuringGuide } from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import PriceDetailsForSSR from '@src/quince-site-common/src/domains/PriceDetailsForSSR'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductOptionValue, {
  ProductOption,
  ProductOptionDisplayType,
  ProductOptionName,
} from '@src/quince-site-common/src/domains/ProductOption'
import { InventoryInfoSourceType, IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { BaseProductVariant, ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import RichText from '@src/quince-site-common/src/domains/RichText'
import { ISwatch } from '@src/quince-site-common/src/domains/Swatch'
import productHelper, {
  checkProductUpholsteredBed,
  getIsWhiteGloveDelivery,
  isProductASofa,
  isProductCaviar,
  isProductVitamin,
  THIRD_PARTY,
} from '@src/quince-site-common/src/helpers/productHelper'
import useKlarnaModule from '@src/quince-site-common/src/hooks/useKlarnaModule'
import useTrueFitWidgetVisibility from '@src/quince-site-common/src/organisms/true-fit/hooks/useTrueFitWidgetVisibility'
import { TrueFitWidget } from '@src/quince-site-common/src/organisms/true-fit/true-fit-widgets'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import productService from '@src/quince-site-common/src/services/productService'
import { GROUP_OPTION_NAMES } from '@src/quince-site-common/src/utils/constants'
import {
  getIsFinalSaleProduct,
  getOverriddenOfferMessage,
} from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import removeTargetAttributeForHashAnchorTags from '@src/quince-site-common/src/utils/removeTargetAttributeForHashAnchorTags'
import OptionSelector from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import useProductPageContext from '@src/quince-site-pdp/src/hooks/useProductContext'
import ShowSimilarLabel from '@src/quince-site-pdp/src/organisms/productPageSections/showSimilar/ShowSimilarLabel'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { find } from '@src/utils/functions/utils'

import { TrackConfig } from '../../hocs/withTracking'
import useHash from '../../hooks/useHash'
import { RichTextProps } from '../../molecules'
import { getIsForcedTextOption } from '../../molecules/optionSelector/productVariantSelector/hooks/useProductVariantSelector.helper'
import AirlineCompatibility from '../airlineCompability/AirlineCompability'
import { default as Button, BUTTON_TYPES } from '../button'
import { default as Icon } from '../icon'
import { default as Modal } from '../modal'
import ProductArrivalMessage from '../productArrivalMessage'
import { ProductIdVsVariantIdMap } from '../productArrivalMessage/ProductArrivalMessage'
import ProductCTA from '../productCTA'
import { CTA_TYPE } from '../productCTA/productCTAConstants'
import ProductMessage from '../productCTA/productMessage'
import useProductCTAType from '../productCTA/useProductCTAType'
import { getUniqueColorGroupings } from '../productCTA/utils'
import { default as ProductInfo } from '../productInfo'
import { default as ProductOfferMessage } from '../productOfferMessage'
import SiblingProductOptions, { SiblingProductOptionType } from '../siblingProductOptions'
import { SizeGuide } from '../sizeGuide'

import Delivery from './icons/delivery.inline.svg'
import Installments from './icons/installments.inline.svg'
import Lock from './icons/lock.inline.svg'
import Return from './icons/return.inline.svg'
import RightArrow from './icons/rightArrow.inline.svg'
import { RightMiniPDPAddToCart } from './MiniPDPRightContainer'
import styles from './productForm.module.scss'
import { ProductOptionProps } from './types'

// @i18n-ignore
const AIRLINE_COMPATIBILITY_HASH = 'airline-compatibility'
const SHOW_MORE_CHARACTER_COUNT = 200

function getProductDescription(description: string, showReadMore: boolean | undefined) {
  if (showReadMore) {
    // Remove all the HTML elements
    const _description = description.replace(/<[^>]*>/g, '')

    if (_description.length > SHOW_MORE_CHARACTER_COUNT) {
      return `<p>${_description.slice(0, SHOW_MORE_CHARACTER_COUNT).trim()}<span data-readmore='true' class='${styles.readMore}'>Read More</span></p>`
    }
  }

  return (
    getSanitizedHtml(description) +
    "<span data-readmore='false' class=" +
    (styles.readLess ?? '') +
    '> Read Less</span>'
  )
}

function shouldShowOnlySizeChart(
  productOptions: ProductOption[] | undefined,
  sizeChart: ContentfulSizeChart | undefined,
  mergedSizeChart: MergedSizeChart[] | undefined,
  hideSizeChart: boolean | undefined,
) {
  const sizeOption = find(productOptions, ({ name }) => name === ProductOptionName.Size)
  const showOnlySizeChart = !hideSizeChart && sizeOption?.isHidden && !!(sizeChart ?? mergedSizeChart)

  return showOnlySizeChart
}

export interface PreOrderInfo {
  date: string
  onPreOrder: () => void
}

export interface MadeToOrderInfo {
  madeToOrderText: RichText
}

export interface VariantPurchaseInfo {
  availableInventory: number
  showWaitList: boolean
  showMadeToOrder: boolean
  hasJoinedWaitList: boolean
  backInStockDate: string | undefined
  madeToOrderText: Document | undefined
  isPreorder: boolean
  preOrderShippingDate?: string
  selectedVariantId?: number
  isNewPreorderProduct?: boolean
  inventoryInfoSource: InventoryInfoSourceType
  selectedInternalVariantId?: number[]
  isAnySelectedVariantInStock?: boolean
  eddDetail?: EDDDetails
}

export interface ProductFormProps {
  className?: string
  title: string
  averageRating?: number
  reviewCount?: number
  productOptions: ProductOption[]
  selectedOptions: ProductVariantOption[]
  isWishListed?: boolean
  formattedSalePrice: string
  festivalOfferPrice?: number
  formattedFestivalOfferPrice?: string
  formattedTraditionalPrice?: string
  productInfo?: RichTextProps
  productInfoHeaderClassName?: string
  onOptionSelect: (optionName: string, selectedValue: string) => void
  onAddToCart: (isPreorder?: boolean) => void
  onWishlistToggle: () => void
  onReviewSummaryClick: () => void
  description?: string
  preOrder?: PreOrderInfo
  hideProductDescription?: boolean
  hideReviewSummary?: boolean
  hideSizeChart?: boolean
  hideOfferMessage?: boolean
  hideMtoText?: boolean
  hideWishListIcon?: boolean
  hidePayLater?: boolean
  hideFooter?: boolean
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  onJoinWaitList: (email?: string | undefined) => void
  sizeChart?: ContentfulSizeChart | undefined
  mergedSizeChart?: MergedSizeChart[] | undefined
  measuringGuides?: ContentfulProductMeasuringGuide[]
  isAddToCartInProgress?: boolean
  isSdcProduct?: boolean
  product?: Product
  errorMessage?: string
  inputOptionError?: string
  optionsContainerClassName?: string
  isReturnable?: boolean
  subDescriptionText?: string
  priceDetails?: PriceDetailsForSSR
  savingPercentage?: number
  offerMessageFromPC?: string
  salePrice: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  selectedSiblingProductId?: number
  onSiblingOptionSelect?: (val: string) => void
  preOrderModalData?: ContentfulPreOrderModal
  disableAddToCart?: boolean
  airlineCompatibilityListings?: CustomSection[] | undefined
  areAllVariantOOS?: boolean
  addToCartRef?: any
  onSwatchLinkClick?: (swatchData: ISwatch[]) => void
  shouldDisableAirlineCompatibility?: boolean
  onBuyNowCart?: () => void
  isCurrentProductOptionLeavingSoon?: boolean
  productPurchaseInfo?: IProductPurchaseInfo
  isBuyNowPdp?: boolean
  isPostOrderFlow?: boolean
  handlePostOrderCallback?: () => void
  isQuickATCForMobile?: boolean
  isKeyboardVisibleForQuickATCForMobile?: boolean
  setIsKeyboardVisibleForQuickATCForMobile?: (isKeyboardVisible: boolean) => void
  onClickedShopSimilar?: () => void
  showNewPdpUI?: boolean
}

const FreeShippingText = ({ isWhiteGloveDelivery }: { isWhiteGloveDelivery: boolean }) => {
  const { t } = useTranslation('common')
  return (
    <div
      className={classnames(styles.container__info__main__text, {
        [styles.container__whiteGloveTextWidth ?? '']: isWhiteGloveDelivery,
      })}
    >
      {isWhiteGloveDelivery ? (
        <>
          {t('free_white')}
          <br /> {t('glove_delivery')}
        </>
      ) : (
        t('free_shipping')
      )}
    </div>
  )
}

const ViewFullProductDetails = ({ text }: { text?: string }) => {
  const { setShowFullPDP } = useProductPageContext()

  function handleClick() {
    setShowFullPDP(true)
    window.scrollTo({
      top: 0,
      // @i18n-ignore
      behavior: 'smooth',
    })
  }

  return (
    <div className={styles.viewFullProdWrapper}>
      <button className={styles.btn} onClick={handleClick}>
        {text ?? 'VIEW FULL PRODUCT DETAILS'}
      </button>
    </div>
  )
}

export const KlarnaWrapper = ({
  hidePayLater,
  salePrice,
  festivalOfferPrice,
  wrapperClassname,
}: {
  hidePayLater?: boolean
  salePrice?: number
  festivalOfferPrice?: number
  wrapperClassname?: string
}) => {
  return !hidePayLater && salePrice ? (
    <div className={classnames(wrapperClassname, styles.container__szmessage)}>
      <klarna-placement
        // @i18n-ignore
        data-key="credit-promotion-auto-size"
        // @i18n-ignore
        data-locale="en-US"
        data-purchase-amount={Math.floor((festivalOfferPrice ?? salePrice) * 100)}
      />
    </div>
  ) : (
    <></>
  )
}

export const ProductDescFooter = ({
  hideFooter,
  isReturnable,
  product,
  selectedOptions,
}: {
  hideFooter: boolean | undefined
  isReturnable: boolean | undefined
  product: Product | undefined
  selectedOptions?: ProductVariantOption[]
}) => {
  const { t } = useTranslation('common')
  const [showEasyReturnModal, setShowEasyReturnModal] = useState(false)
  const secondChargingExpBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING)
  const isInChargingExpBucket = secondChargingExpBucket === 'Show'
  const returnPolicyExpBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.RETURN_POLICY_MESSAGING)

  if (hideFooter) return <></>
  const isWhiteGloveDelivery = getIsWhiteGloveDelivery(product)

  const easyReturnsTrackConfig: TrackConfig = {
    trackGA: true,
    trackInternal: true,
    // @i18n-ignore
    category: 'checkout',
    // @i18n-ignore
    action: 'easy_returns',
    context: {
      product_id: product?.shopifyProductId,
      title: product?.title,
      tags: product?.tags,
      subClassId: product?.subClassId,
      color: selectedOptions?.find(option => option.name === 'Color')?.value,
      size: selectedOptions?.find(option => option.name === 'Size')?.value,
    },
  }

  const handleEasyReturnClick = () => {
    setShowEasyReturnModal(prev => !prev)
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, easyReturnsTrackConfig)
  }

  return (
    <div className={styles.container__info}>
      <div className={styles.container__info__main}>
        <div className={styles.container__info__main__icon}>
          <Icon iconSvg={Delivery} className={styles.container__info__main__icon__delivery} />
        </div>
        <FreeShippingText isWhiteGloveDelivery={!!isWhiteGloveDelivery} />
      </div>
      {isReturnable && !product?.hasRestockingFee ? (
        <div className={styles.container__info__main}>
          <div className={styles.container__info__main__icon}>
            <Icon iconSvg={Return} className={styles.container__info__main__icon__return} />
          </div>
          {isInChargingExpBucket ? (
            <div
              className={classnames(styles.container__info__main__text, styles.easyReturns)}
              onClick={handleEasyReturnClick}
            >
              <p>{t('easy_returns')}</p>
              <span className={styles.iconWrapper}>
                <Icon className={styles.icon} iconSvg={RightArrow} />
              </span>
            </div>
          ) : !product?.isMadeToOrder ? (
            returnPolicyExpBucket ===
            STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.RETURN_POLICY_MESSAGING].buckets?.groupA ? (
              <div className={styles.container__info__main__text}>{t('free_returns')}</div>
            ) : returnPolicyExpBucket ===
              STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.RETURN_POLICY_MESSAGING].buckets?.groupB ? (
              <div className={styles.container__info__main__text}>{t('365_day_returns')}</div>
            ) : (
              <div className={styles.container__info__main__text}>{t('easy_returns')}</div>
            )
          ) : (
            <div className={styles.container__info__main__text}>{t('easy_returns')}</div>
          )}
        </div>
      ) : (
        <div className={styles.container__info__main}>
          <div className={styles.container__info__main__icon}>
            <Installments className={styles.container__info__main__icon__installments} />
          </div>
          <div
            className={classnames(styles.container__info__main__text, {
              [styles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
            })}
          >
            <span>{t('pay_in')}</span> {t('installments')}
          </div>
        </div>
      )}
      <div className={styles.container__info__main}>
        <div className={styles.container__info__main__icon}>
          <Icon iconSvg={Lock} className={styles.container__info__main__icon__lock} />
        </div>
        <div
          className={classnames(styles.container__info__main__text, {
            [styles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
          })}
        >
          <span>{t('secure')} </span>
          {t('payment')}
        </div>
      </div>
      {showEasyReturnModal && isInChargingExpBucket && (
        <Modal
          open={showEasyReturnModal}
          className={styles.easyReturnModal}
          modalWrapperClassName={styles.easyReturnModalWrapper}
          showCloseButton={true}
          onClose={handleEasyReturnClick}
        >
          <div>
            <h3 className={styles.modalHeader}>{t('return_policy')}</h3>
            <div className={styles.subText}>{t('you_are_eligible_for_one_free_return_per_order_15_will_be_dedu')}</div>
            <a
              className={styles.redirectLink}
              // @i18n-ignore
              href={`${window.location.origin}/shipping-returns`}
            >
              {t('learn_more')}
            </a>
          </div>
        </Modal>
      )}
    </div>
  )
}

// eslint-disable-next-line react/display-name
const ProductForm = React.forwardRef(function ProductFormEl(
  props: ProductFormProps,
  ref: ForwardedRef<HTMLDivElement | null>,
) {
  const { t } = useTranslation('common')
  const productFormRef = useRef<HTMLDivElement>(null)

  const newColorGroupings = getUniqueColorGroupings(props.product?.variants ?? [])

  const selectedVariant = useMemo(
    () =>
      props.product
        ? productHelper.getVariantsForOptions(props.product, props.selectedOptions)[0]
        : ({} as BaseProductVariant),
    [props.product, props.selectedOptions],
  )

  useImperativeHandle(ref, () => {
    if (!productFormRef.current) {
      // @i18n-ignore
      throw new Error('productFormRef is not assigned')
    }
    return productFormRef.current
  })

  const {
    showFullPDP,
    setShowFullPDP,
    productPageExpData: {
      productFormWrapperClass,
      moveAddToCartBtnToRight,
      showViewFullProductDetails,
      hideProductDetail,
      hideAddToWishlist,
      hideReviewCount,
      viewFullProductText,
      hideKlarna,
      productInfoHeaderTextClassName,
      hideProductDescFooter,
      hideReviewLabel,
      productFormOptionsClass,
      productInfoReviewClassName,
      inStockMsgClass,
      showReadMoreInDesc,
      showInlineSizeChart,
      hideSizeChart,
    },
  } = useProductPageContext()
  const [showReadMore, setShowReadMore] = useState(showReadMoreInDesc)

  const isFabricMergedProduct = props.product?.addOnProductOptionGroups?.find(
    option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC,
  )
  const initialSwatch = isFabricMergedProduct ? [] : [props.product?.swatch]

  const classes = classnames(styles.container, props.className, productFormWrapperClass)
  const [showSizeChart, setShowSizeChart] = useState(false)
  const [showAirlineCompatibility, setShowAirlineCompatibility] = useState(false)
  const [swatches, setSwatches] = useState(initialSwatch)

  const optionNameSelectedValueMap = props.selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const fetchProductWithSwatch = async () => {
    const products = await productService.getProducts({
      shopifyProductIds: props.product?.addOnProductOptionGroups
        ?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC)
        ?.groupItems.map(item => item.shopifyProductId),
    })
    let currentFabricSelectedSwatch
    const productSwatches = products
      .filter(product => {
        if (product.shopifyProductId == props.product?.shopifyProductId) {
          currentFabricSelectedSwatch = product.swatch
          return false
        } else {
          return true
        }
      })
      .map(product => product.swatch)
    setSwatches([currentFabricSelectedSwatch, ...productSwatches])
  }
  useEffect(() => {
    if (props.product?.addOnProductOptionGroups?.find(option => option.groupOptionName == GROUP_OPTION_NAMES.FABRIC))
      //eslint-disable-next-line
      fetchProductWithSwatch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.product])

  const [hash] = useHash()

  useEffect(() => {
    if (hash === AIRLINE_COMPATIBILITY_HASH) {
      displayAirlineCompatbility()
    }
  }, [hash])

  const handleSizeChart = () => {
    setShowSizeChart(true)
  }

  const displayAirlineCompatbility = () => {
    setShowAirlineCompatibility(true)
  }

  const hideAirlineCompatbility = () => {
    setShowAirlineCompatibility(false)
  }

  const productFormContentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    removeTargetAttributeForHashAnchorTags(productFormContentRef.current)
  }, [])

  // Intersection Observer to check if product summary section is visible in viewport (fires only once)
  useEffect(() => {
    if (!productFormContentRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_SUMMARY_IS_VISIBLE, {
              entity_type: 'action',
              entity_id: 'product_summary',
            })
            observer.disconnect()
          }
        })
      },
      { threshold: 0.2 }, // Trigger when 20% of element is visible
    )

    observer.observe(productFormContentRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  const selectedVariantAvailableInventory = props.selectedVariantPurchaseInfo?.availableInventory ?? 0

  const renderSwatchSelectionButton = useCallback(() => {
    return swatches.filter(item => item).length ? (
      <button
        className={styles.freeSwatchText}
        onClick={() => {
          props.onSwatchLinkClick?.(swatches.filter(item => !!item))
        }}
      >
        {t('order_free_swatches')}
      </button>
    ) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swatches, props.onSwatchLinkClick])

  useKlarnaModule(props.salePrice)

  const { showTruefitWidget } = useTrueFitWidgetVisibility(props.product)

  const addOnOptions = props.product?.addOnProductOptionGroups?.map(group => {
    return {
      node: (
        <SiblingProductOptions
          siblingProductOptionGroup={group}
          displayType={props.onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
          selectedProductId={props.selectedSiblingProductId}
          selectedProductOptions={props.selectedOptions}
          onSelect={props.onSiblingOptionSelect}
          className={classnames(
            styles.container__options,
            props.showNewPdpUI && styles.container__options_adjustedPadding,
          )}
          labelClassName={styles.container__label}
          optionSelectorClassName={classnames(
            styles.container__options__option,
            props.showNewPdpUI && styles.container__options__option_adjustedPadding,
            props.showNewPdpUI &&
              group.groupOptionName !== ProductOptionName.Color &&
              group.groupOptionName !== GROUP_OPTION_NAMES.FABRIC &&
              styles.container__options__option_adjustedPadding_size,
          )}
          optionClassname={styles.container__options__optionWrapper}
        />
      ),
      displaySequence: group.optionDisplaySequence,
      key: group.groupId,
    }
  })

  const _productOptions = props.productOptions
  let updatedProductOptions = _productOptions
  if (newColorGroupings.length && props.product) {
    updatedProductOptions = productHelper.updateProductOptionsWithNewColorOptions(
      props.product,
      props.productOptions,
      newColorGroupings,
      t,
    ) as ProductOption[]

    updatedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
  }

  const colorOptionLastIndex = updatedProductOptions.findLastIndex(po => po.name == ProductOptionName.Color)

  const productOptions = updatedProductOptions
    .filter(po => !po.isHidden)
    .map((productOption, index, arr) => {
      const shouldReduceMarginBottom =
        (newColorGroupings.length && index + 1 <= arr.length - 1 && arr[index + 1]?.name == ProductOptionName.Color) ||
        false

      const shouldShowSwatchButton = colorOptionLastIndex === index && !props.isBuyNowPdp

      return {
        node: renderProductOption({
          productOption: productOption,
          selectedOptions: props.selectedOptions,
          onOptionSelect: props.onOptionSelect,
          sizeChart: props.sizeChart,
          mergedSizeChart: props.mergedSizeChart,
          handleSizeChart: handleSizeChart,
          hideSizeChart: props.hideSizeChart ?? hideSizeChart,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          renderSwatchSelectionButton: shouldShowSwatchButton ? renderSwatchSelectionButton : () => null,
          optionsContainerClassName: props.optionsContainerClassName,
          productFormOptionsClass: productFormOptionsClass,
          shouldReduceMarginBottom: shouldReduceMarginBottom,
          showInlineSizeChart: showInlineSizeChart,
          showTruefitWidget: showTruefitWidget,
          product: props.product,
          t: t,
          productPurchaseInfo: props.productPurchaseInfo,
          showNewPdpUI: props.showNewPdpUI,
          inputOptionError: props.inputOptionError,
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }
    })

  const combinedProductOptions = [...(addOnOptions ?? []), ...productOptions]
  combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)

  const productCTAType = useProductCTAType({
    selectedVariantPurchaseInfo: props.selectedVariantPurchaseInfo,
  })

  const offerMessage = getOverriddenOfferMessage(props.offerMessageFromPC, props.product, props.selectedOptions)

  const isProductCaviarFlag = props.product ? isProductCaviar(props.product) : false

  const isFinalSaleProduct = getIsFinalSaleProduct(props.product, props.selectedOptions)

  const selectedVariantIdsByProductId = useMemo(() => {
    if (!props.product?.id) return {}

    return {
      [props.product.id]: props.selectedVariantPurchaseInfo?.selectedInternalVariantId ?? [],
    }
  }, [props.product?.id, props.selectedVariantPurchaseInfo?.selectedInternalVariantId])

  const variantIdsByProductId = useMemo(
    () => (props.product?.id ? { [props.product.id]: props.product.variants?.map(variant => variant.id) } : {}),
    [props.product?.id, props.product?.variants],
  )

  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < 5

  const shouldShowDeliveryDetails = props.product ? productHelper.shouldShowDeliveryInformation(props.product) : false

  const showOnlySizeChart = shouldShowOnlySizeChart(
    props.productOptions,
    props.sizeChart,
    props.mergedSizeChart,
    props.hideSizeChart ?? hideSizeChart,
  )
  const isProductSofa = isProductASofa(props.product)
  const isProductUpholsteredBed = checkProductUpholsteredBed(props.product)

  const shouldShowMTOText =
    !props.hideMtoText &&
    props.selectedVariantPurchaseInfo?.showMadeToOrder &&
    props.selectedVariantPurchaseInfo.madeToOrderText &&
    !isProductSofa &&
    !isProductUpholsteredBed

  const shippingInfoText = props.product && productHelper.getShippingInfoText(props.product)

  const isVitamin = isProductVitamin(props.product)
  const hideSavings = props.product?.hideSavings ?? false
  return (
    <>
      <div ref={productFormRef} className={classes}>
        <ProductInfo
          productInfoHeaderClassName={props.productInfoHeaderClassName}
          hideReviewSummary={props.hideReviewSummary}
          productTitle={props.title}
          formattedSalePrice={props.formattedSalePrice}
          formattedTraditionalPrice={props.formattedTraditionalPrice}
          isWishListed={props.isWishListed}
          avgRating={props.averageRating}
          reviewCount={props.reviewCount}
          onWishListToggle={props.onWishlistToggle}
          hideWishListIcon={props.hideWishListIcon ?? hideAddToWishlist}
          onReviewSummaryClick={props.onReviewSummaryClick}
          savingPercentage={props.savingPercentage}
          brandName={props.brandName}
          brandLink={props.brandLink}
          traditionalDiscountMessage={props.traditionalDiscountMessage}
          formattedFestivalOfferPrice={props.formattedFestivalOfferPrice}
          showFullPDP={showFullPDP}
          hideReviewCount={hideReviewCount}
          hideReviewLabel={hideReviewLabel}
          productInfoHeaderTextClassName={productInfoHeaderTextClassName}
          productInfoReviewClassName={productInfoReviewClassName}
          showDiscount={selectedVariant?.vendorType !== THIRD_PARTY}
          pricePerServing={selectedVariant?.pricePerServing}
          traditionalPricePerServing={selectedVariant?.traditionalPricePerServing}
          isVitamin={isVitamin}
          hideSavings={hideSavings}
        />
        {combinedProductOptions.map(option => (
          <Fragment key={option.key}>{option.node}</Fragment>
        ))}
        {showOnlySizeChart && renderSizeChartButton(handleSizeChart, t)}
        {(shouldShowMTOText ?? !props.isReturnable) && (
          <div className={styles.container__mtoText}>
            {shouldShowMTOText ? (
              <>
                {props.selectedVariantPurchaseInfo?.madeToOrderText
                  ? documentToReactComponents(
                      props.selectedVariantPurchaseInfo.madeToOrderText,
                      documentToReactComponentsTextOptions,
                    )
                  : null}
              </>
            ) : (
              shippingInfoText && (
                //eslint-disable-next-line
                <div dangerouslySetInnerHTML={{ __html: getSanitizedHtml(shippingInfoText as string) }} />
              )
            )}
            {!props.isReturnable && (
              <div className={classnames(styles.container__mtoText__subText, 'd-block')}>
                {t('this_product_is_not_returnable')}
              </div>
            )}
          </div>
        )}
        {/* <EarlyShipmentTimeLabel
                 productId={props?.product?.shopifyProductId}
                 varient={EarlyShipmentTimeLabel.VARIENT.PRODUCT_FORM}
               /> */}
        {props.subDescriptionText && (
          <div
            className={classnames(
              styles.container__subDescText,
              props.showNewPdpUI && styles.container__subDescText_adjustedPadding,
            )}
            data-testid="sub-description-text"
          >
            {
              <div
                className={classnames(styles.container__subDescText__subText, {
                  [`${styles.container__subDescText__textColorBlack}`]: props.isReturnable,
                })}
                //eslint-disable-next-line
                dangerouslySetInnerHTML={{ __html: getSanitizedHtml(props.subDescriptionText) }}
              />
            }
          </div>
        )}
        <ProductOfferMessage
          hideOfferMessage={props.hideOfferMessage}
          offerMessageFromPC={offerMessage}
          productId={props.product?.shopifyProductId}
          showFinalSaleMessage={isFinalSaleProduct}
        />
        {CONFIG_ENABLE_HOLIDAY_CUTOFF &&
        !productHelper.isGiftCard(props.product?.shopifyProductId) &&
        !props.selectedVariantPurchaseInfo?.isPreorder &&
        shouldShowDeliveryDetails &&
        props.selectedVariantPurchaseInfo?.isAnySelectedVariantInStock ? (
          <ProductArrivalMessage
            allProductIdsVsVariantIdsMap={variantIdsByProductId as ProductIdVsVariantIdMap}
            selectedProductIdsVsVariantIdsMap={selectedVariantIdsByProductId}
          />
        ) : null}
        <ProductMessage
          type={productCTAType}
          selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
          preOrderModalData={props.preOrderModalData}
          customMessage={
            isProductCaviarFlag ? t('caviar_will_be_delivered_with_overnight_shipping_between_april_1') : undefined
          }
        />
        {showVariantAvailableInventory && (
          <div
            className={classnames(
              styles.selected_variant_available_inventory_message,
              props.showNewPdpUI && styles.selected_variant_available_inventory_message_adjustedPadding,
              props.isQuickATCForMobile && styles.selected_variant_available_inventory_message__quickATC,
            )}
          >
            {t('hurry_only')} {selectedVariantAvailableInventory} {t('left')}
          </div>
        )}
        {!moveAddToCartBtnToRight && (
          <>
            {props.errorMessage && (
              <div
                className={classnames(
                  styles.container__error_message,
                  props.isQuickATCForMobile && styles.container__error_message__quickATC,
                )}
              >
                {props.errorMessage}
              </div>
            )}
            <ProductCTA
              type={productCTAType}
              onAddToCart={props.onAddToCart}
              isAddToCartInProgress={props.isAddToCartInProgress}
              selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
              onJoinWaitList={props.onJoinWaitList}
              showVariantAvailableInventory={showVariantAvailableInventory}
              product={props.product}
              areAllVariantOOS={props.areAllVariantOOS}
              addToCartRef={props.addToCartRef}
              onBuyNow={props.onBuyNowCart}
              isCurrentProductOptionLeavingSoon={props.isCurrentProductOptionLeavingSoon}
              isBuyNowPdp={props.isBuyNowPdp}
              isPostOrderFlow={props.isPostOrderFlow}
              handlePostOrderCallback={props.handlePostOrderCallback}
              isQuickATCForMobile={props.isQuickATCForMobile}
              isKeyboardVisibleForQuickATCForMobile={props.isKeyboardVisibleForQuickATCForMobile}
              setIsKeyboardVisibleForQuickATCForMobile={props.setIsKeyboardVisibleForQuickATCForMobile}
              onClickedShopSimilar={props.onClickedShopSimilar}
              showNewPdpUI={props.showNewPdpUI}
              disableAddToCart={props.disableAddToCart}
            />
          </>
        )}
        {showViewFullProductDetails ? <ViewFullProductDetails text={viewFullProductText} /> : <></>}
        {moveAddToCartBtnToRight ? null : (
          <>
            {!hideKlarna && (
              <KlarnaWrapper
                hidePayLater={props.hidePayLater}
                salePrice={props.salePrice}
                festivalOfferPrice={props.festivalOfferPrice}
              />
            )}
            <ShowSimilarLabel showIfAvailable={!!(props.preOrder ?? props.selectedVariantPurchaseInfo?.showWaitList)} />
            {!hideProductDescFooter && (
              <ProductDescFooter
                hideFooter={props.hideFooter}
                isReturnable={props.isReturnable}
                product={props.product}
                selectedOptions={props.selectedOptions}
              />
            )}
          </>
        )}
        {hideProductDetail
          ? null
          : !props.hideProductDescription &&
            props.description && (
              <div
                className={classnames(styles.container__wrapper, {
                  [styles.showReadMore ?? '']: showReadMore,
                  [styles.showReadMoreInDesc ?? '']: showReadMoreInDesc,
                })}
              >
                <div
                  className={styles.container__content}
                  ref={productFormContentRef}
                  onClick={e => {
                    if ('readmore' in (e.target as HTMLElement).dataset) {
                      if ((e.target as HTMLElement).dataset.readmore === 'true') {
                        setShowReadMore(false)
                      } else {
                        setShowReadMore(true)
                      }
                    }
                  }}
                  //eslint-disable-next-line
                  dangerouslySetInnerHTML={{
                    __html: getProductDescription(props.description, showReadMore),
                  }}
                ></div>
              </div>
            )}
      </div>
      <RightMiniPDPAddToCart
        shouldShow={moveAddToCartBtnToRight}
        hidePayLater={props.hidePayLater}
        salePrice={props.salePrice}
        festivalOfferPrice={props.festivalOfferPrice}
        type={productCTAType}
        onAddToCart={props.onAddToCart}
        isAddToCartInProgress={props.isAddToCartInProgress}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        onJoinWaitList={props.onJoinWaitList}
        showVariantAvailableInventory={showVariantAvailableInventory}
        product={props.product}
        hideFooter={props.hideFooter}
        isReturnable={props.isReturnable}
        setShowFullPDP={setShowFullPDP}
        hideKlarna={hideKlarna}
        errorMessage={props.errorMessage}
        inStockMsgClass={inStockMsgClass}
      />
      {props.sizeChart && (
        <SizeGuide
          showSizeChartModal={showSizeChart}
          sizeChart={props.sizeChart}
          mergedSizeChart={props.mergedSizeChart ?? []}
          onSizeChartModalClose={() => {
            setShowSizeChart(false)
          }}
          measuringGuides={props.measuringGuides ?? []}
        />
      )}
      {!props.shouldDisableAirlineCompatibility && showAirlineCompatibility && (
        <AirlineCompatibility
          show={showAirlineCompatibility}
          onCloseAirlineCompatbility={hideAirlineCompatbility}
          airlineCompatibilityListingCS={props.airlineCompatibilityListings}
        />
      )}
    </>
  )
})

const renderSizeChartButton = (
  handleSizeChart: (e: React.MouseEvent<HTMLButtonElement>) => void,
  t: TFunction,
  showInlineSizeChart?: boolean,
  showNewPdpUI?: boolean,
) => (
  <Button
    type={BUTTON_TYPES.SECONDARY_LINK}
    onClick={handleSizeChart}
    className={classnames(
      styles.container__label__sizeChart,
      showNewPdpUI && styles.container__label__sizeChart_adjustedPadding,
      {
        [styles.sizeChart_show ?? '']: showInlineSizeChart,
      },
    )}
    id={TEST_LOCATORS.SIZE_CHART_BUTTON}
  >
    {t('size_chart')}
  </Button>
)

export const documentToReactComponentsTextOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => {
      // eslint-disable-next-line react/no-children-prop
      return React.Children.map(children, (child: React.ReactNode, index: number) => {
        if (typeof child === 'string') {
          // eslint-disable-next-line
          return <span key={index} dangerouslySetInnerHTML={{ __html: getSanitizedHtml(child) }} />
        }
        return child
      })
    },
  },
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => {
      return typeof text === 'string' ? (
        // eslint-disable-next-line
        <span className="font-weight--500" dangerouslySetInnerHTML={{ __html: getSanitizedHtml(text) }} />
      ) : (
        text
      )
    },
  },
}

// @i18n-ignore
const FIRMNESS_DISPLAY_NAME = 'Firmness'
// @i18n-ignore
const VALUE_DISPLAY_NAME = 'Value'

export const renderProductOption = (props: ProductOptionProps) => {
  const {
    selectedOptions,
    onOptionSelect,
    sizeChart,
    mergedSizeChart,
    handleSizeChart,
    hideSizeChart,
    optionNameSelectedValueMap,
    renderSwatchSelectionButton,
    optionsContainerClassName,
    productFormOptionsClass,
    shouldReduceMarginBottom,
    showInlineSizeChart,
    showTruefitWidget,
    product,
    legendClassName,
    optionSelectorClassName,
    optionDisplayTypesVsClassName,
    t,
    productPurchaseInfo,
    showNewPdpUI,
    inputOptionError,
  } = props
  let productOption = props.productOption
  if (getIsForcedTextOption(product?.id)) {
    productOption = new ProductOption({
      ...productOption,
      values: productOption.values?.map(v => {
        return { ...v, displayType: ProductOptionDisplayType.TEXT }
      }),
    })
  }
  const poValueDisplayNameMap = productOption.values?.reduce((map: Record<string, string>, option) => {
    map[option.value] = option.displayName ?? option.value
    return map
  }, {})
  // TODO(STRFRNT-2867) - remove this after release
  let optionValues: ProductOptionValue[] = []
  const isCustomizationInputOption =
    productOption.name === ProductOptionName.Customization && productOption.displayType === 'Input'
  const isEngravingInputOption =
    productOption.name === ProductOptionName.Engraving && productOption.displayType === 'Text_box'

  if (isCustomizationInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.DEPRECATED_INPUT,
        value: '',
      }),
    ]
  } else if (isEngravingInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.INPUT,
        value: '',
        maxCharacterLimit: productOption.maxCharacterLimit,
        minCharacterLimit: productOption.minCharacterLimit,
      }),
    ]
  } else {
    optionValues = productOption.values ?? []
  }

  if (productOption.isHidden) return null

  // Hardcoding required as productOption.Name comes from API and can be used for tracking
  // Firmness productOptionName is color
  const isAllRoundOptions = (() => {
    const excludedDisplayNames = [FIRMNESS_DISPLAY_NAME, VALUE_DISPLAY_NAME]
    return productOption.name === ProductOptionName.Color && !excludedDisplayNames.includes(productOption.displayName)
  })()
  const optionSelectedValue = optionNameSelectedValueMap[productOption.name]

  return (
    <fieldset
      className={classnames(
        styles.container__options,
        !showNewPdpUI && optionsContainerClassName,
        productFormOptionsClass,
        {
          [styles.container__options__colorOptionGroup ?? '']: shouldReduceMarginBottom,
        },
        showNewPdpUI && styles.container__options_adjustedPadding,
      )}
      key={productOption.name}
    >
      <legend
        data-testid={`legend_${productOption.displayName}`}
        className={classnames(styles.container__label, legendClassName)}
      >
        {productOption.displayName || productOption.name}
        {/* TODO(STRFRNT-2867) - remove this after release */}
        {productOption.displayType === 'Text Box'
          ? t('var1_v6', { var1: optionNameSelectedValueMap[productOption.name] ?? '' })
          : ''}
        {productOption.displayType === 'Input' || productOption.displayType === 'Text Box' ? '' : ': '}
        {(optionSelectedValue && poValueDisplayNameMap?.[optionSelectedValue]) ?? (
          <span className="sr-only">{t('not_selected')}</span>
        )}
        {!hideSizeChart &&
          productOption.name === ProductOptionName.Size &&
          (sizeChart ?? mergedSizeChart) &&
          renderSizeChartButton(handleSizeChart, t, showInlineSizeChart, showNewPdpUI)}
      </legend>
      <OptionSelector
        className={classnames(
          styles.container__options__option,
          optionSelectorClassName,
          showNewPdpUI && styles.container__options__option_adjustedPadding,
          showNewPdpUI &&
            productOption.name !== ProductOptionName.Color &&
            styles.container__options__option_adjustedPadding_size,
        )}
        optionValueList={optionValues}
        selectedValue={optionNameSelectedValueMap[productOption.name]}
        onSelect={value => {
          onOptionSelect(productOption.name, value)
        }}
        isAllRoundOptions={isAllRoundOptions}
        product={product}
        selectorKey={QUINCE_BOT_LOCATORS.PDP.FIRST_PRODUCT_OPTIONS}
        selectedOptions={selectedOptions}
        isPDP={true}
        optionDisplayTypesVsClassName={optionDisplayTypesVsClassName}
        renderAfterColorComponent={renderSwatchSelectionButton}
        displayName={productOption.displayName}
        productPurchaseInfo={productPurchaseInfo}
        inputOptionError={inputOptionError}
      />
      {!showInlineSizeChart &&
        !hideSizeChart &&
        productOption.name === ProductOptionName.Size &&
        (sizeChart ?? mergedSizeChart) && (
          <>
            {showTruefitWidget ? (
              <TrueFitWidget
                productId={product?.id}
                colorId={getSelectedColorFromSelectedOptions(selectedOptions)}
                useDataRec
                // @i18n-ignore
                parentContainerId="product_purchase"
              />
            ) : null}
          </>
        )}
    </fieldset>
  )
}

export const renderErrorMessage = (errorMessage?: string, className?: string) => {
  return <div className={classnames(styles.container__error_message, className)}>{errorMessage}</div>
}

export default ProductForm
/* eslint-enable i18next/no-literal-string */
